import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { Layers, ChevronLeft, Target, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import PeriodFilter from "../components/PeriodFilter";
import { fetchByCategory, fetchBySubcategory, fetchCategories } from "../lib/api";
import { eur, personColor } from "../lib/format";

const PALETTE = ["#D26A50", "#4A6D5E", "#E2C08D", "#6B5CA5", "#729B79", "#F4A261", "#2A9D8F", "#E76F51", "#E9C46A", "#264653", "#9C6F45", "#A26769"];
const CHART_MARGIN_H = { left: 20, right: 20 };
const CHART_MARGIN_V = { left: 0, right: 20, top: 10 };
const TICK_STYLE = { fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const CURSOR_STYLE = { fill: "rgba(0,0,0,0.03)" };

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-xl bg-white border border-stone-200 shadow-lg p-3 text-sm">
      {label && <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-stone-600">{p.name || p.dataKey}:</span>
          <span className="tabular font-semibold">{eur(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function DashboardCategories() {
  const [period, setPeriod] = useState({ year: undefined, month: undefined });
  const [person, setPerson] = useState(undefined);
  const [categoryData, setCategoryData] = useState([]);
  const [drilldown, setDrilldown] = useState(null); // category name
  const [subData, setSubData] = useState([]);
  const [colorMap, setColorMap] = useState({});

  useEffect(() => {
    fetchCategories().then((cats) => {
      const map = {};
      cats.forEach((c) => { if (c.color) map[c.name] = c.color; });
      setColorMap(map);
    });
  }, []);

  useEffect(() => {
    fetchByCategory({ ...period, person }).then(setCategoryData);
  }, [period.year, period.month, person]);

  useEffect(() => {
    if (drilldown) {
      fetchBySubcategory({ category: drilldown, ...period, person }).then(setSubData);
    } else {
      setSubData([]);
    }
  }, [drilldown, period.year, period.month, person]);

  const total = useMemo(() => categoryData.reduce((s, r) => s + r.total, 0), [categoryData]);
  const colorFor = (cat, i = 0) => colorMap[cat] || PALETTE[i % PALETTE.length];
  const pieData = useMemo(() => categoryData.map((c, i) => ({ name: c.category, value: c.total, fill: colorFor(c.category, i) })), [categoryData, colorMap]);
  const budgetedCategories = useMemo(
    () => categoryData.filter((c) => c.budget !== null && c.budget !== undefined),
    [categoryData],
  );
  const showBudgets = Boolean(period.year && period.month);

  return (
    <div className="space-y-8" data-testid="dashboard-categories">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Dashboard 2</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">Analisi per categoria</h1>
          <p className="text-stone-600 mt-2 max-w-xl">Esplora le spese per categoria e sotto-categoria, e confronta come Madda e Doio si dividono le uscite.</p>
        </div>
        <PeriodFilter idPrefix="cat" year={period.year} month={period.month} onChange={setPeriod} includePerson person={person} onPersonChange={setPerson} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Budget alerts — visibili solo quando è selezionato uno specifico mese e ci sono budget configurati */}
        {showBudgets && budgetedCategories.length > 0 && (
          <div className="card-soft p-8 lg:col-span-12" data-testid="budget-alerts-card">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3 text-stone-500">
                <Target size={18} strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-widest">Budget del mese</span>
              </div>
              <Link
                to="/budget"
                className="text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 pill-btn px-3 py-1.5"
                data-testid="budget-alerts-link"
              >
                Gestisci budget →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {budgetedCategories.map((c) => {
                const ratio = c.budget > 0 ? c.total / c.budget : 0;
                const pct = Math.round(ratio * 100);
                const over = c.over_budget;
                const warn = !over && ratio >= 0.8;
                const color = over ? "#C0392B" : warn ? "#E67E22" : "#4A6D5E";
                const label = over ? "Sforato" : warn ? "Attenzione" : "Sotto soglia";
                return (
                  <div
                    key={c.category}
                    className="rounded-2xl border border-stone-200 bg-white p-5"
                    data-testid={`budget-alert-${c.category}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-display text-base font-semibold">{c.category}</div>
                      <span
                        className="text-xs uppercase tracking-widest flex items-center gap-1.5 font-medium"
                        style={{ color }}
                      >
                        {over || warn ? <AlertTriangle size={13} strokeWidth={2} /> : <CheckCircle2 size={13} strokeWidth={2} />}
                        {label}
                      </span>
                    </div>
                    <div className="text-xs text-stone-500 mb-2">
                      Speso <span className="tabular font-semibold text-stone-700">{eur(c.total)}</span>
                      {" "}su <span className="tabular font-semibold text-stone-700">{eur(c.budget)}</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-stone-100 overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${Math.min(100, ratio * 100)}%`, background: color }}
                        data-testid={`budget-alert-bar-${c.category}`}
                      />
                    </div>
                    <div className="mt-2 text-xs text-stone-500 tabular text-right" data-testid={`budget-alert-pct-${c.category}`}>
                      {pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pie chart of totals by category */}
        <div className="card-soft p-8 lg:col-span-5" data-testid="category-pie-card">
          <div className="flex items-center gap-3 text-stone-500 mb-4">
            <Layers size={18} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest">Ripartizione totale</span>
          </div>
          <div className="relative h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={2}>
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} stroke="#FAF9F6" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-display text-2xl font-semibold tabular" data-testid="pie-total">{eur(total)}</div>
              <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">Totale</div>
            </div>
          </div>
        </div>

        {/* Bar chart Madda vs Doio per category */}
        <div className="card-soft p-8 lg:col-span-7" data-testid="category-bar-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 text-stone-500">
              <Layers size={18} strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest">Confronto per categoria</span>
            </div>
            <div className="text-xs text-stone-400">Clic su una barra per i dettagli</div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical" margin={CHART_MARGIN_H}>
                <CartesianGrid stroke="#EBE8E1" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" stroke="#6E6B67" tick={TICK_STYLE} />
                <YAxis type="category" dataKey="category" width={120} stroke="#6E6B67" tick={TICK_STYLE} />
                <Tooltip content={<ChartTooltip />} cursor={CURSOR_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey="Madda" stackId="a" fill="#D26A50" onClick={(d) => setDrilldown(d.category)} radius={[0, 0, 0, 0]} />
                <Bar dataKey="Doio" stackId="a" fill="#4A6D5E" onClick={(d) => setDrilldown(d.category)} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category list as buttons */}
        <div className="card-soft p-8 lg:col-span-12" data-testid="category-list-card">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Seleziona una categoria</div>
          <div className="flex flex-wrap gap-2">
            {categoryData.map((c, i) => {
              const active = drilldown === c.category;
              return (
                <button
                  key={c.category}
                  data-testid={`drill-${c.category}`}
                  onClick={() => setDrilldown(active ? null : c.category)}
                  className={`pill-btn px-4 py-2 text-sm font-medium border ${active ? "bg-[#377D22] text-white border-[#2E5F1B]" : "bg-white border-stone-200 hover:bg-stone-50"}`}
                  style={!active ? { borderLeft: `4px solid ${colorFor(c.category, i)}` } : {}}
                >
                  {c.category} · <span className="tabular ml-1">{eur(c.total)}</span>
                </button>
              );
            })}
            {categoryData.length === 0 && <div className="text-stone-400 text-sm">Nessuna spesa nel periodo selezionato</div>}
          </div>
        </div>

        {/* Drilldown */}
        {drilldown && (
          <div className="card-soft p-8 lg:col-span-12" data-testid="subcategory-detail">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <button onClick={() => setDrilldown(null)} className="pill-btn p-2 hover:bg-stone-100" data-testid="drilldown-back">
                  <ChevronLeft size={18} strokeWidth={1.5} />
                </button>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">Categoria</div>
                  <div className="font-display text-2xl font-semibold">{drilldown}</div>
                </div>
              </div>
              <div className="text-sm text-stone-600">
                Totale: <span className="tabular font-semibold">{eur(subData.reduce((s, r) => s + r.total, 0))}</span>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={subData} margin={CHART_MARGIN_V}>
                  <CartesianGrid stroke="#EBE8E1" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="subcategory" stroke="#6E6B67" tick={TICK_STYLE} interval={0} angle={-20} textAnchor="end" height={70} />
                  <YAxis stroke="#6E6B67" tick={TICK_STYLE} />
                  <Tooltip content={<ChartTooltip />} cursor={CURSOR_STYLE} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Bar dataKey="Madda" fill="#D26A50" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Doio" fill="#4A6D5E" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-stone-500 text-left">
                    <th className="py-3">Sotto-categoria</th>
                    <th className="py-3 text-right">Madda</th>
                    <th className="py-3 text-right">Doio</th>
                    <th className="py-3 text-right">Totale</th>
                    <th className="py-3 text-right">N°</th>
                  </tr>
                </thead>
                <tbody>
                  {subData.map((s) => (
                    <tr key={s.subcategory} className="border-t border-stone-100" data-testid={`sub-row-${s.subcategory}`}>
                      <td className="py-3">{s.subcategory}</td>
                      <td className="py-3 tabular text-right" style={{ color: personColor("Madda") }}>{eur(s.Madda)}</td>
                      <td className="py-3 tabular text-right" style={{ color: personColor("Doio") }}>{eur(s.Doio)}</td>
                      <td className="py-3 tabular text-right font-semibold">{eur(s.total)}</td>
                      <td className="py-3 tabular text-right text-stone-500">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
